import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

/**
 * Per-route SEO metadata: <title>, <meta name="description">, and the
 * full OpenGraph + Twitter Card set. Mounted ONCE inside the router
 * (alongside RouteCanonical) so navigation updates the head without
 * any per-page boilerplate.
 *
 * Extending this:
 *   - Add a key to ROUTE_SEO matching the pathname.
 *   - For dynamic routes (e.g. /c/:username), call getRouteSeo() with
 *     a custom override prop or extend buildSeo() with pattern logic.
 *
 * Routes not in ROUTE_SEO fall back to "default" — generic site-wide
 * copy. Auth-gated/transient routes (/app, /admin, etc.) still get
 * the noindex tag from RouteCanonical, so they won't compete in
 * search results regardless of the title shown here.
 */

const SITE_NAME = "Coach AI";
const SITE_URL = "https://coachaifit.com";
const DEFAULT_OG_IMAGE = `${SITE_URL}/opengraph-image.png`;
const TWITTER_HANDLE = "@CoachAIFit";

type SeoEntry = {
  title: string;
  description: string;
  /** Override `og:image`. Defaults to the site-wide OG image. */
  image?: string;
  /** OG type — "website" for marketing pages, "article" for content. */
  ogType?: "website" | "article";
};

// Public, indexable routes. Keep keys in sync with CANONICAL_ROUTES in
// src/components/RouteCanonical.tsx and the sitemap.
const ROUTE_SEO: Record<string, SeoEntry> = {
  "/": {
    title: "Virtual Fitness Coach & AI Training Plans — Coach AI",
    description:
      "Coach AI is a virtual fitness coach: AI-built training and meal plans, weekly check-ins and form analysis, with a real coach reviewing your plan. Book a coach or start free.",
  },
  "/pricing": {
    title: "Pricing — Coach AI",
    description:
      "Simple monthly and yearly plans for Coach AI. Cancel anytime, save with annual billing, and unlock the full coaching toolkit.",
  },
  "/auth": {
    title: "Sign in — Coach AI",
    description:
      "Sign in to your Coach AI account to manage clients, review check-ins, and pick up where you left off.",
  },
  "/signup-client": {
    title: "Join your coach on Coach AI",
    description:
      "Redeem your invite code and create a Coach AI client account to start receiving plans, check-ins, and feedback.",
  },
  "/about": {
    title: "About — Coach AI",
    description:
      "Coach AI is an AI-powered coaching platform for competitors and everyday lifters — posing analysis, meal plans, workouts, and food scanning.",
  },
  "/personalized-workout-plans": {
    title: "Personalized Workout Plan — Coach AI",
    description:
      "Get a personalized workout plan built around your goal, schedule, and equipment — with meals to match and an AI coach that adapts day to day.",
  },
  "/how-it-works": {
    title: "How Coach AI Fit Works — The Coach & Client Loop",
    description:
      "See how Coach AI Fit works: you set the brief, AI drafts your training and meals, your coach reviews and edits, you log, and the plan adapts between check-ins.",
  },
  "/how-we-use-ai": {
    title: "How Coach AI Fit Uses AI — The Meal Plan Generator",
    description:
      "How Coach AI Fit uses AI: what the meal plan generator reads, how calorie and macro targets are set, how meals are written and checked, and where a coach steps in.",
  },
  "/ai-nutrition-plan": {
    title: "AI Nutrition Plan Generator — Free Meal Plan",
    description:
      "Enter your goal, body stats and food preferences and get a free personalised AI nutrition plan: calorie and macro targets plus a three day sample meal plan.",
  },
  "/ai-personal-trainer": {
    title: "AI Personal Trainer — Coach AI",
    description:
      "An AI personal trainer that builds your training and meals from your own data, answers questions instantly, and can be reviewed by a real coach.",
  },
  "/ai-workout-generator": {
    title: "AI Workout Generator — Coach AI",
    description:
      "Generate a structured training plan from your goal, schedule, experience and equipment — with progression, exercise demos and optional coach review.",
  },
  "/ai-posing-coach": {
    title: "AI Posing Coach — Bodybuilding Pose Analysis | Coach AI",
    description:
      "Upload a photo or video of a mandatory pose and get feedback judged against your division, with measured joint angles, shoulder line and left-to-right symmetry.",
  },
  "/portal": {
    title: "My Account — Coach AI",
    description:
      "Sign in to your Coach AI client portal to view your training and meal programs, payments, invoices and coach messages.",
  },
  "/guides": {
    title: "Training & Nutrition Guides — Coach AI",
    description:
      "Practical guides on AI personal trainers, AI workout generators, progression and nutrition — written for people who actually train.",
  },
  "/athlete-portal": {
    title: "Athlete Portal — Your Plans, Payments & Coach Messages | Coach AI",
    description:
      "The Coach AI athlete portal puts your training and meal plans, payments, invoices and coach messages in one place. Free to open, seven day free trial.",
  },
  "/locations": {
    title: "Virtual Fitness Coach by City — Atlanta, Brisbane & More",
    description:
      "Coach AI works wherever you train. See how virtual fitness coaching works in Atlanta GA, Brisbane QLD and surrounding cities, with real coach review.",
  },
  "/blog": {
    title: "Athlete Journeys — Coach AI Blog",
    description:
      "Real training journeys from Coach AI athletes: prep diaries, what changed week to week, and the plans behind the progress.",
  },
  "/coaches": {
    title: "Find a Coach — Coach AI Coach Directory",
    description:
      "Browse Coach AI coaches, see who they work with and what they specialise in, then book the virtual fitness coach who fits your goal.",
  },
  "/terms": {
    title: "Terms of Service — Coach AI",
    description:
      "The terms that apply when you use Coach AI: your account, subscriptions and billing, coaching content, acceptable use and cancellation.",
  },
  "/privacy": {
    title: "Privacy Policy — Coach AI",
    description:
      "How Coach AI handles your data: what we collect for training and nutrition plans, how progress photos are stored, who can see it, and how to delete it.",
  },
};

const DEFAULT_SEO: SeoEntry = {
  title: "Coach AI — AI coaching platform for trainers and athletes",
  description:
    "Coach AI gives athletes AI meal and training plans, food and progress tracking and a chat coach, and gives trainers one place to build plans, review check-ins and manage clients.",
};

function normalizePath(pathname: string): string {
  return pathname !== "/" && pathname.endsWith("/")
    ? pathname.slice(0, -1)
    : pathname;
}

export function RouteSeo() {
  const { pathname } = useLocation();
  const key = normalizePath(pathname);
  const entry = ROUTE_SEO[key] ?? DEFAULT_SEO;
  const image = entry.image ?? DEFAULT_OG_IMAGE;
  const ogType = entry.ogType ?? "website";
  const url = `${SITE_URL}${key === "/" ? "/" : key}`;

  return (
    <Helmet>
      <title>{entry.title}</title>
      <meta name="description" content={entry.description} />

      {/* OpenGraph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={entry.title} />
      <meta property="og:description" content={entry.description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={entry.title} />
      <meta name="twitter:description" content={entry.description} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
}
